import { createSelector, createFeatureSelector } from '@ngrx/store';
import {
	jobDetailAdapter,
	JobDetailsState,
	jobDetailFeatureKey,
} from './job-detail.reducer';
import type { Job, ConfirmPrevailingWageResponse, Railroad, RailroadOvertimeBasis, JobOverTimeBasis } from 'src/shared/api';
import { getAllRailroadEntities } from 'src/admin-app/railroads/railroad-detail/+state/railroad-detail.selectors';
import { getAllCustomerEntities } from 'src/admin-app/customers/customer-detail/+state/customer-detail.selectors';
import { railroadToOptionalJobFieldsToShow } from 'src/admin-app/shared/optional-fields/optional-fields.utility';
import { buildListMessage } from 'src/admin-app/utilities/list-filtering/list-message';

export const getJobDetailState = createFeatureSelector<JobDetailsState>(
	jobDetailFeatureKey
);

const { selectEntities } = jobDetailAdapter.getSelectors();

export const getLoadingStatus = createSelector(
	getJobDetailState,
	(state: JobDetailsState) => state.isLoading
);

export const getEditBillingInfo = createSelector(
	getJobDetailState,
	(state: JobDetailsState) => state.editBillingInfo
);

export const getSelectedJobId = createSelector(
	getJobDetailState,
	(state: JobDetailsState) => state.selectedJobId
);

export const getRequireJobDates = createSelector(
	getJobDetailState,
	(state: JobDetailsState) => !state.allowTbdJobDates
);

export const getAllJobEntities = createSelector(
	getJobDetailState,
	selectEntities
);

export const getSelectedJob = createSelector(
	getAllJobEntities,
	getSelectedJobId,
	(jobEntities, jobId) => jobEntities[jobId]
);

export const getTitle = createSelector(getSelectedJob, (job) => {
	let title = '';
	if (job) {
		if (job.id > 0) {
			title = `Edit Job ${job.number}`;
		} else {
			title = 'Create Job';
		}
		if (job.isNonBillable) {
			title += ' - Non-Billable';
		}
	}

	return title;
});

export const getJobEntity = (id: number) =>
	createSelector(getAllJobEntities, (entities) => entities[id]);

export const getCloseAfterSaving = createSelector(
	getJobDetailState,
	(state: JobDetailsState) => !state.shouldStayOnPage
);

export const getOtherCharges = createSelector(
	getJobDetailState,
	(state) => state.otherCharges
);

export const getIsSaving = createSelector(
	getJobDetailState,
	(state: JobDetailsState) => state.isSaving
);

export const getError = createSelector(
	getJobDetailState,
	(state: JobDetailsState) => state.error
);

export const getShouldSaveButtonShake = createSelector(
	getJobDetailState,
	(state: JobDetailsState) => state.error && !state.shouldStayOnPage
);

const getRailroadIdForJob = createSelector(
	getSelectedJob,
	getJobDetailState,
	(job: Job, state: JobDetailsState) => {
		let railroadId = state.railroadId;
		if (!railroadId && job && job.railroad) {
			railroadId = job.railroad.id;
		}

		return railroadId;
	}
);

const getCustomerIdForJob = createSelector(
	getSelectedJob,
	getJobDetailState,
	(job: Job, state: JobDetailsState) => {
		let customerId = state.customerId;
		if (!customerId && job && job.customer) {
			customerId = job.customer.id;
		}

		return customerId;
	}
);

export const getRailroadForJob = createSelector(
	getAllRailroadEntities,
	getRailroadIdForJob,
	(railroadEntities, railroadId) =>
		railroadEntities[railroadId] ? railroadEntities[railroadId] : null
);

export const getOfferedServices = createSelector(
	getRailroadForJob,
	(railroad) => (railroad ? railroad.offeredServices : [])
);

export const getOptionalJobFields = createSelector(
	getRailroadForJob,
	(railroad) =>
		railroad ? railroadToOptionalJobFieldsToShow(railroad) : null
);

export const getCustomerForJob = createSelector(
	getAllCustomerEntities,
	getCustomerIdForJob,
	(customerEntities, customerId) =>
		customerEntities[customerId] ? customerEntities[customerId] : null
);

export const getDivision = createSelector(
	getJobDetailState,
	(state: JobDetailsState) => state.division
);

export const getDivisionOptions = createSelector(
	getRailroadForJob,
	(railroad) => (railroad ? railroad.divisions : [])
);

export const getSubdivisionOptions = createSelector(getDivision, (division) =>
	division ? division.subdivisions : []
);

export const hasOptionalJobFields = createSelector(
	getOptionalJobFields,
	(fields) => fields && Object.values(fields).some((f) => !!f)
);

export const getPrevailingWageConfirmation = createSelector(
	getSelectedJob,
	(quote) => {
		let confirmation: ConfirmPrevailingWageResponse;
		if (
			quote &&
			quote.prevailingWageInfo &&
			quote.prevailingWageInfo.confirmedBy &&
			quote.prevailingWageInfo.confirmedDate
		) {
			confirmation = {
				confirmedBy: quote.prevailingWageInfo.confirmedBy,
				confirmedDate: quote.prevailingWageInfo.confirmedDate,
			};
		}
		return confirmation;
	}
);

export const getRegularTimeBasis = createSelector(
	getRailroadForJob,
	(railroad) => (railroad ? railroad.regularTimeBasis : null)
);

export const getOvertimeBasis = createSelector(getRailroadForJob, (railroad) =>
	railroad ? railroad.overtimeBasis : null
);

export const getShowServiceType = createSelector(getSelectedJob, (job) =>
	job ? (job.offeredServices || []).length > 1 || (job.contractedRates || []).length > 1 : false
);

export const getIsNonBillableJob = createSelector(getSelectedJob, (job) =>
	job ? job.isNonBillable : false
);

export const areCostCentersEnabled = createSelector(
	getIsNonBillableJob,
	getRailroadForJob,
	(isNonBillable, railroad) =>
		!isNonBillable && railroad?.areCostCentersEnabled
);

export const getQuoteLogListMessage = createSelector(
	getJobDetailState,
	(state) =>
		buildListMessage(
			state.error,
			state.isQuoteLogLoading,
			state.pagedQuoteLogResults
		)
);

export const getQuoteLogs = createSelector(getJobDetailState, (state) =>
	state.pagedQuoteLogResults ? state.pagedQuoteLogResults.results : []
);

export const isQuoteLogInitialized = createSelector(
	getJobDetailState,
	(state) => state.isQuoteLogInitialized);

export const getJobLogListMessage = createSelector(getJobDetailState, (state) =>
	buildListMessage(
		state.error,
		state.isJobLogLoading,
		state.pagedJobLogResults
	)
);

export const getJobLogs = createSelector(getJobDetailState, (state) =>
	state.pagedJobLogResults ? state.pagedJobLogResults.results : []
);


export const isOvertimeDivergedFromRailroad = createSelector(
	getSelectedJob,
	getRailroadForJob,
	(job: Job, railroad: Railroad) => {
		if (railroad?.overtimeBasis && job?.overTimeBasis) {

			const railroadOvertime: keyof typeof RailroadOvertimeBasis = railroad.overtimeBasis;
			const jobOvertime: keyof typeof JobOverTimeBasis = job.overTimeBasis;

			return railroadOvertime !== jobOvertime;
		}

		return false;
	}
);
